import { create } from "zustand";
import type {
  MessageResponseDto,
  SenderIdentity,
} from "@/lib/tanstack-query/dtos/messaging.dto";

export interface TypingActor {
  identity: SenderIdentity | "ANON";
  userId?: string;
  expiresAt: number;
}

export interface PresenceActor {
  identity: SenderIdentity | "ANON";
  userId?: string;
  online: boolean;
}

export type PendingSendStatus = "pending" | "failed";

export interface PendingSend {
  clientId: string;
  conversationId: string;
  body: string;
  attachment: File | null;
  identity: SenderIdentity;
  status: PendingSendStatus;
  createdAt: string;
  error?: string;
}

interface MessagingState {
  isSocketConnected: boolean;
  unreadCount: number;
  typingByConv: Record<string, TypingActor[]>;
  presenceByConv: Record<string, PresenceActor[]>;
  pendingByConv: Record<string, PendingSend[]>;
  activeConversationId: string | null;
  setSocketConnected: (connected: boolean) => void;
  setUnreadCount: (count: number) => void;
  bumpUnread: (delta: number) => void;
  setActiveConversation: (id: string | null) => void;
  setTyping: (
    conversationId: string,
    actor: Omit<TypingActor, "expiresAt"> & { ttlMs?: number },
  ) => void;
  clearExpiredTyping: () => void;
  setPresence: (conversationId: string, actor: PresenceActor) => void;
  enqueuePending: (send: PendingSend) => void;
  markPendingFailed: (
    conversationId: string,
    clientId: string,
    error: string,
  ) => void;
  removePending: (conversationId: string, clientId: string) => void;
  reconcilePendingWithServer: (message: MessageResponseDto) => void;
  reset: () => void;
}

export const useMessagingStore = create<MessagingState>()((set) => ({
  isSocketConnected: false,
  unreadCount: 0,
  typingByConv: {},
  presenceByConv: {},
  pendingByConv: {},
  activeConversationId: null,

  setSocketConnected: (connected) => set({ isSocketConnected: connected }),
  setUnreadCount: (count) => set({ unreadCount: Math.max(0, count) }),
  bumpUnread: (delta) =>
    set((s) => ({ unreadCount: Math.max(0, s.unreadCount + delta) })),
  setActiveConversation: (id) => set({ activeConversationId: id }),

  setTyping: (conversationId, { identity, userId, ttlMs = 3000 }) =>
    set((s) => {
      const expiresAt = Date.now() + ttlMs;
      const existing = s.typingByConv[conversationId] ?? [];
      const filtered = existing.filter((t) =>
        identity === "SELF" || identity === "ANON"
          ? !(t.identity === identity && t.userId === userId)
          : t.identity !== identity,
      );
      return {
        typingByConv: {
          ...s.typingByConv,
          [conversationId]: [...filtered, { identity, userId, expiresAt }],
        },
      };
    }),

  clearExpiredTyping: () =>
    set((s) => {
      const now = Date.now();
      const next: Record<string, TypingActor[]> = {};
      for (const [convId, actors] of Object.entries(s.typingByConv)) {
        const remaining = actors.filter((a) => a.expiresAt > now);
        if (remaining.length > 0) next[convId] = remaining;
      }
      return { typingByConv: next };
    }),

  setPresence: (conversationId, actor) =>
    set((s) => {
      const existing = s.presenceByConv[conversationId] ?? [];
      const filtered = existing.filter((p) =>
        actor.identity === "SELF" || actor.identity === "ANON"
          ? !(p.identity === actor.identity && p.userId === actor.userId)
          : p.identity !== actor.identity,
      );
      return {
        presenceByConv: {
          ...s.presenceByConv,
          [conversationId]: [...filtered, actor],
        },
      };
    }),

  enqueuePending: (send) =>
    set((s) => {
      const existing = s.pendingByConv[send.conversationId] ?? [];
      const filtered = existing.filter((p) => p.clientId !== send.clientId);
      return {
        pendingByConv: {
          ...s.pendingByConv,
          [send.conversationId]: [...filtered, send],
        },
      };
    }),

  markPendingFailed: (conversationId, clientId, error) =>
    set((s) => ({
      pendingByConv: {
        ...s.pendingByConv,
        [conversationId]: (s.pendingByConv[conversationId] ?? []).map((p) =>
          p.clientId === clientId ? { ...p, status: "failed", error } : p,
        ),
      },
    })),

  removePending: (conversationId, clientId) =>
    set((s) => ({
      pendingByConv: {
        ...s.pendingByConv,
        [conversationId]: (s.pendingByConv[conversationId] ?? []).filter(
          (p) => p.clientId !== clientId,
        ),
      },
    })),

  reconcilePendingWithServer: (message) =>
    set((s) => {
      const pending = s.pendingByConv[message.conversationId] ?? [];
      if (pending.length === 0) return s;
      const now = new Date(message.createdAt).getTime();
      const matchIndex = pending.findIndex((p) => {
        if (p.status !== "pending") return false;
        const pTime = new Date(p.createdAt).getTime();
        return (
          Math.abs(now - pTime) < 60_000 && p.body === (message.body ?? "")
        );
      });
      if (matchIndex === -1) return s;
      return {
        pendingByConv: {
          ...s.pendingByConv,
          [message.conversationId]: pending.filter((_, i) => i !== matchIndex),
        },
      };
    }),

  reset: () =>
    set({
      isSocketConnected: false,
      unreadCount: 0,
      typingByConv: {},
      presenceByConv: {},
      pendingByConv: {},
      activeConversationId: null,
    }),
}));
